import { Languages } from '@livv/models';

const OPENING_QUOTES = ['\u00AB', '\u201C', '"'] as const;
const CLOSING_QUOTES = ['\u00BB', '\u201D', '"'] as const;

export const getQuoteMarks = (
    language: Languages,
): {
    quoteOpen: string;
    quoteClose: string;
} => {
    const isFr = language === Languages.fr;

    return {
        quoteOpen: isFr ? '\u00AB\u00A0' : '\u201C',
        quoteClose: isFr ? '\u00A0\u00BB' : '\u201D',
    };
};

const isBlockquoteLine = (line: string): boolean => /^\s*>/.test(line);

const parseBlockquoteLine = (line: string) => {
    const match = /^(\s*>\s?)(.*)$/.exec(line);
    if (!match) {
        return {
            content: line,
            isBlank: line.trim().length === 0,
            line,
            prefix: '',
        };
    }

    const [, prefix, content] = match;

    return {
        content,
        isBlank: content.trim().length === 0,
        line,
        prefix,
    };
};

const hasOpeningQuote = (content: string): boolean => {
    const trimmed = content.trimStart();
    if (!trimmed) return false;

    return OPENING_QUOTES.some((quote) => trimmed.startsWith(quote));
};

const hasClosingQuote = (content: string): boolean => {
    const trimmed = content.trimEnd();
    if (!trimmed) return false;

    return CLOSING_QUOTES.some((quote) => trimmed.endsWith(quote));
};

const TRAILING_CITATIONS_RE = /((?:[\s\u00A0\u202F\u2007]*\[\d+\](?:\([^)]*\))?)+)(\s*)$/;

const extractTrailingCitations = (
    content: string,
): { body: string; citations: string; trailing: string } => {
    const match = TRAILING_CITATIONS_RE.exec(content);
    if (!match) {
        return { body: content, citations: '', trailing: '' };
    }

    const [fullMatch, citations, trailing = ''] = match;
    const body = content.slice(0, -fullMatch.length);

    return { body, citations, trailing };
};

const addOpeningQuote = (content: string, quoteOpen: string): string => {
    if (hasOpeningQuote(content)) return content;

    const leadingSpaces = content.match(/^\s*/)?.[0] ?? '';

    return `${leadingSpaces}${quoteOpen}${content.slice(leadingSpaces.length)}`;
};

const addClosingQuote = (content: string, quoteClose: string): string => {
    const { body, citations, trailing } = extractTrailingCitations(content);
    const core = body.replace(/\s*$/, '');

    if (hasClosingQuote(core)) return content;

    return `${core}${quoteClose}${citations}${trailing}`;
};

export const autoQuoteBlockquotes = (markdown: string, language: Languages): string => {
    if (!markdown) return markdown;

    const { quoteOpen, quoteClose } = getQuoteMarks(language);
    const lines = markdown.split(/\r?\n/);
    const output: string[] = [];

    let index = 0;
    while (index < lines.length) {
        const line = lines[index];
        if (!isBlockquoteLine(line)) {
            output.push(line);
            index += 1;
            continue;
        }

        const blockStart = index;
        while (index < lines.length && isBlockquoteLine(lines[index])) {
            index += 1;
        }

        const blockLines = lines.slice(blockStart, index);
        const parsed = blockLines.map(parseBlockquoteLine);

        let cursor = 0;
        while (cursor < parsed.length) {
            if (parsed[cursor].isBlank) {
                output.push(parsed[cursor].line);
                cursor += 1;
                continue;
            }

            const paragraphStart = cursor;
            while (cursor < parsed.length && !parsed[cursor].isBlank) {
                cursor += 1;
            }
            const paragraphEnd = cursor;

            const firstLine = parsed[paragraphStart];
            const lastLine = parsed[paragraphEnd - 1];

            const firstContent = addOpeningQuote(firstLine.content, quoteOpen);
            const lastContent = addClosingQuote(
                paragraphStart === paragraphEnd - 1 ? firstContent : lastLine.content,
                quoteClose,
            );

            for (let i = paragraphStart; i < paragraphEnd; i += 1) {
                const entry = parsed[i];
                let nextContent = entry.content;
                if (i === paragraphStart) nextContent = firstContent;
                if (i === paragraphEnd - 1) nextContent = lastContent;
                output.push(`${entry.prefix}${nextContent}`);
            }
        }
    }

    return output.join('\n');
};
